import { ChevronLeft, ChevronRight, Infinity } from 'lucide-react';

interface PaginationControlsProps {
  mode: 'pagination' | 'infinite';
  onModeChange: (mode: 'pagination' | 'infinite') => void;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
}

export function PaginationControls({
  mode,
  onModeChange,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 15,
  onPageChange,
  onLoadMore,
  hasMore = false,
  isLoading = false,
}: PaginationControlsProps) {
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        {/* Mode Toggle */}
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-600">View mode:</span>
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onModeChange('pagination')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mode === 'pagination'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pagination
            </button>
            <button
              onClick={() => onModeChange('infinite')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center ${
                mode === 'infinite'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Infinity className="w-4 h-4 mr-1" />
              Infinite Scroll
            </button>
          </div>
        </div>

        {/* Pagination Controls */}
        {mode === 'pagination' && totalItems > 0 && (
          <div className="flex items-center space-x-4">
            <p className="text-sm text-gray-600">
              Showing {startIndex}-{endIndex} of {totalItems}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange?.(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-3">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => onPageChange?.(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Infinite Scroll Load More */}
        {mode === 'infinite' && hasMore && (
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className="btn-secondary flex items-center"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                Loading...
              </>
            ) : (
              <>
                <Infinity className="w-4 h-4 mr-2" />
                Load More
              </>
            )}
          </button>
        )}

        {mode === 'infinite' && !hasMore && totalItems > 0 && (
          <p className="text-sm text-gray-600">
            All {totalItems} items loaded
          </p>
        )}
      </div>
    </div>
  );
}
